import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import AddAnotherAccountPermanentPromotionBox from 'src/components/AddAnotherAccountPromotionBox/AddAnotherAccountPromotionBox'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import NotificationsSummaryBox from 'src/components/NotificationsSummaryBox/NotificationsSummaryBox'
import PermanentPromotionBox from 'src/components/PermanentPromotionBox/PermanentPromotionBox'
import ProfileQuicklinksBox from 'src/components/ProfileQuicklinksBox/ProfileQuicklinksBox'
import ProfileSummaryBox from 'src/components/ProfileSummaryBox/ProfileSummaryBox'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import { ImageNames, OrganizationRoles } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { registerAccountUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { getNotificationSettings } from 'src/redux/actions/data/notificationSettings.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectNotificationSettingListModelList } from 'src/redux/selectors/data/notificationSettings.selectors'
import { selectCreditCardsActiveSourceCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'

const ProfileOverview: React.FC = () => {
  const user = useSelector(selectUserListModelFromState)
  const notificationSettings = useSelector(
    selectNotificationSettingListModelList,
  )
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveSourceCount = useSelector(
    selectCreditCardsActiveSourceCountStatisticsFromState,
  )
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    showGuestRole && user?.organizationRole === OrganizationRoles.guest
  const { REGISTER_ACCOUNT: canRegisterAccount } =
    useQueryUserFeatures([UserFeature.REGISTER_ACCOUNT]).data || {}
  const account = useColorOrGrayscaleImage(ImageNames.Account)

  const dispatch = useBackgroundDispatch()
  const dispatchGetNotificationSettings = (): void => {
    dispatch(getNotificationSettings())
  }

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveSourceCountStatistics,
    ])
  }, [dispatchStatistics])

  useEffect((): void => {
    dispatchGetNotificationSettings()
  }, [])

  return user !== undefined ? (
    !statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded ? (
      <LoadingBackgroundPage />
    ) : (
      <BackgroundPageContent>
        <HorizontalContentCollection>
          <VerticalContentCollection>
            <ProfileSummaryBox user={user} />
            {notificationSettings.length > 0 && ( // TODO: better way than this conditional?
              <NotificationsSummaryBox
                notificationSettings={notificationSettings}
              />
            )}
            {!isGuestUser &&
              canRegisterAccount &&
              (creditCardsActiveSourceCount > 0 ? (
                <AddAnotherAccountPermanentPromotionBox />
              ) : (
                <PermanentPromotionBox
                  imageSrc={account}
                  title={<Trns source={'Want to send virtual cards?'} />}
                  subtitle={
                    <Trns
                      source={
                        'Register your company credit card and start creating virtual cards in minutes.'
                      }
                    />
                  }
                  navAway={{
                    id: 'regAcc',
                    label: <Trns source={'Register an account'} />,
                    to: registerAccountUrl(),
                    ariaLabelSource: 'Register an account',
                  }}
                />
              ))}
          </VerticalContentCollection>
          <VerticalContentCollection>
            <ProfileQuicklinksBox />
          </VerticalContentCollection>
        </HorizontalContentCollection>
      </BackgroundPageContent>
    )
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default ProfileOverview
